import { useMemo, useState } from "react";
import { ExportAccountsOptions } from "types/enums";
import { ExportCompany, UrlResponse } from "../../../../types/apiTypes";
import { SerializedError } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { INPUT_REGEX } from "../../../../utils/regex";
import { ReuseModal } from "components/ui/ReuseModal";
import { Box, Stack, Typography } from "@mui/material";
import { SubBlock } from "../../export-data/SubBlock";
import { SelectedExportOptions } from "../../export-data/SelectedExportOption";
import { ReuseInput } from "../../ReuseInput";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { openLinkHandler } from "../../../../utils/openLinkHandler";
import { ModalListWithSelect } from "../../ModalListWithSelect";

const DEFAULT_FILE_NAME = "Export-data";

export const ExportCSVWithInfinityScrollModal = ({
  title,
  description,
  submitBtnText,
  isLoading,
  items,
  onClose,
  onSubmit,
  canLoadMore,
  loadMore,
  setSearchHandler,
  page,
  isFetching,
  startPage,
}: {
  title: string;
  description: string;
  submitBtnText: string;
  items: ExportCompany[];
  onClose: () => void;
  canLoadMore: boolean;
  isLoading: boolean;
  isFetching: boolean;
  page: number;
  loadMore: () => void;
  setSearchHandler: (search: string) => void;
  startPage: number;
  onSubmit: (
    ids: string[],
    fileName: string,
    isSelectedAll: boolean,
  ) => Promise<UrlResponse>;
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [isSelectedAll, setIsSelectedAll] = useState<boolean>(true);

  const [isError, setIsError] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>(DEFAULT_FILE_NAME);
  const [option, setOption] = useState<string>(ExportAccountsOptions.all);

  const isSelectedAllOption = option === ExportAccountsOptions.all;

  const successHandler = ({ url }: UrlResponse) => {
    openLinkHandler(url, fileName);
    onClose();
  };

  const errorHandler = (
    error: FetchBaseQueryError | SerializedError | undefined,
  ) => {
    toast.error(
      <CustomToast
        type={"error"}
        text={
          getErrorMessage(error).globalError ||
          "Something went wrong. Please try again"
        }
        title={"Export Data Failed"}
      />,
    );
  };

  const submitHandler = async () => {
    onSubmit(selected, fileName, isSelectedAllOption || isSelectedAll)
      .then(successHandler)
      .catch(errorHandler);
  };

  const inputChangeHandler = (value: string) => {
    setIsError(!value.trim() || !INPUT_REGEX.test(value));
    setFileName(value);
  };

  const isDisabled =
    isError ||
    (selected.length === 0 && option === ExportAccountsOptions.selected) ||
    isLoading;

  const prepareAccounts = useMemo(
    () =>
      items.map(({ id, name }) => ({
        id,
        name,
        missed: 0,
      })),
    [items],
  );

  return (
    <ReuseModal
      isOpen={true}
      isLoading={isLoading}
      onClose={onClose}
      title={title}
      onSubmit={submitHandler}
      maxWidth="540px"
      submitBtnColor="#1C3C6C"
      submitBtnText={submitBtnText}
      isDisabled={isDisabled}
      isDisabledCancelBtn={isDisabled}
    >
      <Stack gap="16px">
        <SubBlock title="" text={description} />
        <SelectedExportOptions
          option={option}
          setOption={setOption}
          isSelectedAll={isSelectedAll}
          selectedItemList={selected}
        />
        {option === ExportAccountsOptions.selected && (
          <ModalListWithSelect
            page={page}
            loadMore={loadMore}
            isLoading={isFetching}
            canLoadMore={canLoadMore}
            itemsList={prepareAccounts}
            withSelectAll
            withHeader={false}
            setIsSelectedAll={setIsSelectedAll}
            setSelectedHandler={setSelected}
            setSearchHandler={setSearchHandler}
            startPage={startPage}
          />
        )}

        <SubBlock title="File Name" text="What should be the CSV file name?" />
        <Stack
          direction="row"
          gap="8px"
          alignItems="center"
          pt="16px"
          paddingBottom="35px"
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
            }}
          >
            <ReuseInput
              name="name"
              handleInputChange={(key, value) => inputChangeHandler(value)}
              errorMessage={
                "Must contain only letters, numbers, and basic punctuation"
              }
              value={fileName}
              maxLength={50}
              label="File Name*"
              placeholder=" e.g., INFUSE"
              isError={isError}
              setErrorHandler={(key, value) => setIsError(value)}
            />
          </Box>

          <Typography
            component="span"
            variant="body1"
            sx={{ fontWeight: 500, lineHeight: "26px" }}
            color="#4C545B"
          >
            .csv
          </Typography>
        </Stack>
      </Stack>
    </ReuseModal>
  );
};
